import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DamageEstimator from "@/components/DamageEstimator";
import DocumentOCR from "@/components/DocumentOCR";
import { Upload, FileText, Trash2, Loader2 } from "lucide-react";

const CATEGORIES = ["RC", "Driving License", "Insurance Policy", "FIR", "Accident Photo", "Repair Estimate", "Aadhaar", "Other"];

// Map document category → OCR doc_type
const OCR_TYPE = {
  "RC": "rc",
  "Driving License": "driving_license",
  "Insurance Policy": "policy",
};

export default function DocumentVault() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState(CATEGORIES[0]);

  const load = () => {
    setLoading(true);
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => setDocs([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      load();
    } catch (e) {
      alert("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this document?")) return;
    await api.delete(`/documents/${id}`);
    load();
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] mesh-subtle py-10" data-testid="vault-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-800 dark:text-blue-400 mb-2">Document Vault</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
            Your secure document vault
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">Encrypted storage tied to your account. Upload once, use across all claims. RC / DL / Policy uploads get instant AI OCR.</p>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-3 items-center">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-700 outline-none sm:w-56"
              data-testid="vault-category"
            >
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <label className="btn-signature inline-flex items-center gap-2 rounded-xl px-5 py-3 font-semibold cursor-pointer flex-1 justify-center" data-testid="vault-upload-button">
              {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Choose File</>}
              <input type="file" className="hidden" disabled={uploading} onChange={(e) => upload(e.target.files?.[0])} />
            </label>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-3 text-center sm:text-left">JPG, PNG, PDF up to 10MB · End-to-end stored on secure storage · AI OCR runs on-demand</p>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800">
            <h2 className="font-heading font-bold text-lg text-slate-900 dark:text-white">All documents ({docs.length})</h2>
          </div>
          {loading ? (
            <div className="p-10 text-center text-slate-500">Loading…</div>
          ) : docs.length === 0 ? (
            <div className="p-10 text-center" data-testid="vault-empty">
              <FileText className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
              <p className="text-slate-700 dark:text-slate-300 font-semibold">No documents yet</p>
              <p className="text-sm text-slate-500 mt-1">Upload your RC, DL or policy to get started.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {docs.map((d) => {
                const ocrType = OCR_TYPE[d.category];
                const isImage = (d.content_type || "").startsWith("image/");
                return (
                  <li key={d.doc_id} className="px-6 py-4" data-testid={`vault-row-${d.doc_id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-700/15 text-blue-800 dark:text-blue-400 flex items-center justify-center shrink-0">
                          <FileText className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 dark:text-white truncate">{d.original_filename}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">{d.category} · {(d.size / 1024).toFixed(1)} KB · {new Date(d.created_at).toLocaleDateString()}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => remove(d.doc_id)}
                          className="p-2 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-500/10 text-slate-400 hover:text-rose-500"
                          data-testid={`vault-delete-${d.doc_id}`}
                          aria-label="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {d.category === "Accident Photo" && isImage && (
                      <DamageEstimator docId={d.doc_id} />
                    )}
                    {ocrType && isImage && (
                      <DocumentOCR docId={d.doc_id} docType={ocrType} category={d.category} lastExtraction={d.last_extraction} />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
